/**
 * Liveness heartbeat for the Sensor Manager's Web Serial connection.
 *
 * Once connected, nothing re-checks that the device is still there — an unplug,
 * reset, or crash leaves the UI thinking it is connected. This runs a periodic
 * PING and, after a run of consecutive misses, reports the connection lost so
 * the caller can flip the badge, notify, and trigger reconnect.
 *
 * Self-sequencing: it pings, awaits the result, waits `intervalMs`, then pings
 * again — so pings never stack up behind a slow/congested serial command queue.
 * A single miss never declares loss (the queue may be briefly busy with a
 * readings poll); only `maxMisses` CONSECUTIVE misses do. Any success resets
 * the counter.
 *
 * @param {object}   opts
 * @param {() => Promise} opts.ping        one round-trip liveness check (e.g. client.ping)
 * @param {() => void}    opts.onLost      called once when the connection is declared lost
 * @param {number}        [opts.intervalMs=5000]  gap between pings
 * @param {number}        [opts.maxMisses=2]       consecutive misses that declare loss
 */
export function createConnectionHeartbeat({ ping, onLost, intervalMs = 5000, maxMisses = 2 }) {
  let timer = null;
  let stopped = true;
  let misses = 0;
  let inFlight = false;

  const scheduleNext = () => {
    if (stopped) return;
    timer = setTimeout(tick, intervalMs);
  };

  async function tick() {
    if (stopped || inFlight) return;
    inFlight = true;
    let ok = false;
    try {
      await ping();
      ok = true;
    } catch (_) {
      ok = false;
    }
    inFlight = false;
    if (stopped) return;

    if (ok) {
      misses = 0;
      scheduleNext();
      return;
    }

    misses += 1;
    if (misses >= maxMisses) {
      stop(); // stop before onLost so no ping races the teardown it triggers
      onLost();
      return;
    }
    scheduleNext();
  }

  function stop() {
    stopped = true;
    clearTimeout(timer);
    timer = null;
  }

  return {
    start() {
      if (!stopped) return; // already running
      stopped = false;
      misses = 0;
      scheduleNext(); // first ping after one interval — the link was just validated
    },
    stop,
  };
}
